// src/components/principal/YourPhotosGrid.jsx (v8 – uses getMediaUrls helper everywhere)

import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../services/api";
import { API_PATHS } from "../../config";
import timeAgo from "../../utils/timeAgo";
import { getMediaUrls } from "../../utils/media";

const PAGE_SIZE = 20;

const YourPhotosGrid = ({ onError, active = true }) => {
  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [story, setStory] = useState("");

  const observerRef = useRef(null);

  const inFlightRef = useRef(false);
  const activeRef = useRef(active);
  const hasMoreRef = useRef(hasMore);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const lastPhotoRef = useCallback((node) => {
    if (!activeRef.current) return;

    if (observerRef.current) observerRef.current.disconnect();
    if (!node || !hasMoreRef.current || inFlightRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;

        if (inFlightRef.current) return;
        if (!hasMoreRef.current) return;
        if (!activeRef.current) return;

        setPage((p) => p + 1);
      },
      { threshold: 0.5 }
    );

    observerRef.current.observe(node);
  }, []);

  useEffect(() => {
    if (!active) return;
    if (!hasMore) return;
    if (inFlightRef.current) return;

    const fetchPhotos = async () => {
      inFlightRef.current = true;
      setLoading(true);

      try {
        const { data } = await api.get(API_PATHS.MY_PHOTOS, {
          params: {
            my_photos: true,
            offset: page * PAGE_SIZE,
            limit: PAGE_SIZE,
          },
        });

        const newPhotos = Array.isArray(data) ? data : [];
        setPhotos((prev) => (page === 0 ? newPhotos : [...prev, ...newPhotos]));
        setHasMore(newPhotos.length === PAGE_SIZE);
      } catch (e) {
        if (e?.response?.status === 429) {
          setHasMore(false);
          onError?.("Too many requests — please wait a moment and try again.");
        } else {
          onError?.("Failed to load your photos.");
        }
      } finally {
        setLoading(false);
        inFlightRef.current = false;
      }
    };

    fetchPhotos();
  }, [page, active, hasMore, onError]);

  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = null;
    };
  }, []);

  const startEdit = (photo) => {
    setEditingId(photo.id);
    setStory(photo.story || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setStory("");
  };

  const saveStory = async (photoId) => {
    const trimmed = story.trim();
    if (!trimmed) {
      onError?.("Story cannot be empty.");
      return;
    }

    try {
      await api.post(`/photos/${photoId}/update-story`, { new_story: trimmed });

      setPhotos((prev) =>
        prev.map((p) => (p.id === photoId ? { ...p, story: trimmed } : p))
      );
      setEditingId(null);
      setStory("");
    } catch (e) {
      onError?.(e.response?.data?.detail || "Failed to update story.");
    }
  };

  if (!active) return null;

  if (loading && photos.length === 0) {
    return (
      <div className="principal-panel photos-content">
        <p className="loading-indicator">Loading your photos…</p>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="principal-panel photos-content">
        <p>You haven’t uploaded any approved photos yet.</p>
      </div>
    );
  }

  return (
    <div className="principal-panel photos-content">
      <div className="photos-grid">
        {photos.map((photo, index) => {
          const { isVideo, imageUrl, videoUrl, posterUrl } = getMediaUrls(photo);

          const durationSeconds =
            photo.video_duration_ms > 0
              ? Math.round(photo.video_duration_ms / 1000)
              : null;

          return (
            <div
              key={photo.id}
              className="principal-item-card photo-card"
              ref={index === photos.length - 1 ? lastPhotoRef : null}
            >
              {isVideo ? (
                videoUrl ? (
                  <div className="photo-video-wrapper">
                    <video
                      src={videoUrl}
                      poster={posterUrl}
                      className="photo-thumbnail"
                      muted
                      playsInline
                      controls
                    />
                    <div className="photo-video-pill">
                      <span>▶ Video</span>
                      {durationSeconds !== null && (
                        <span className="photo-video-duration">{durationSeconds}s</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="photo-missing-media">Video file not available.</div>
                )
              ) : imageUrl ? (
                <img
                  src={imageUrl}
                  alt={photo.story || "Your photo"}
                  className="photo-thumbnail"
                  loading="lazy"
                />
              ) : (
                <div className="photo-missing-media">Image not available.</div>
              )}

              {editingId === photo.id ? (
                <div className="edit-story-form">
                  <textarea
                    value={story}
                    onChange={(e) => setStory(e.target.value)}
                    placeholder="Edit story and hashtags"
                    className="form-textarea"
                    maxLength={255}
                    rows={4}
                  />
                  <div className="edit-actions">
                    <button
                      onClick={() => saveStory(photo.id)}
                      className="save-story-button"
                    >
                      Save
                    </button>
                    <button onClick={cancelEdit} className="cancel-story-button">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="photo-story">{photo.story || "No story added"}</p>
                  <p className="photo-meta">
                    Uploaded {photo.created_at ? timeAgo(photo.created_at) : "N/A"}
                    {isVideo && (
                      <>
                        {" • "}Video
                        {durationSeconds !== null && ` · ${durationSeconds}s`}
                      </>
                    )}
                  </p>
                  <button
                    onClick={() => startEdit(photo)}
                    className="edit-story-button"
                  >
                    Edit Story
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>

      {loading && <p className="loading-indicator">Loading more photos…</p>}
      {!hasMore && photos.length > 0 && (
        <p className="no-more-photos">No more photos to load.</p>
      )}
    </div>
  );
};

export default YourPhotosGrid;